import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CropMonitorRecord } from '@/types/api';
import { cropApi } from '@/services/cropApi';
import DashboardHeader from './components/DashboardHeader';
import LatestDataPanel from './components/LatestDataPanel';
import CameraControl from './components/CameraControl';
import DeviceSettings from './components/DeviceSettings';
import ChartPanel from './components/ChartPanel';
import HistoryTable from './components/HistoryTable';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [deviceId, setDeviceId] = useState('');
  const [latest, setLatest] = useState<CropMonitorRecord | null>(null);
  const [selectedData, setSelectedData] = useState<CropMonitorRecord | null>(null);
  const [totalCnt, setTotalCnt] = useState(0);
  const [housingHeightMm, setHousingHeightMm] = useState(2200);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLatest = useCallback(async (id: string) => {
    try {
      const res = await cropApi.getLatest(id);
      if (res.result === 'success' && res.data) {
        setLatest(res.data);
        if (res.housing_height_mm) {
          setHousingHeightMm(res.housing_height_mm);
        }
      } else {
        setLatest(null);
      }
    } catch {
      setError('최신 데이터를 불러오지 못했습니다.');
    }
  }, []);

  useEffect(() => {
    const id = localStorage.getItem('device_id');
    if (!id) {
      navigate('/');
      return;
    }
    setDeviceId(id);

    const init = async () => {
      setLoading(true);
      await loadLatest(id);
      setLoading(false);
    };
    init();
  }, [navigate, loadLatest]);

  const handleSelectRecord = useCallback((record: CropMonitorRecord) => {
    setSelectedData(record);
  }, []);

  const handleBackToLatest = useCallback(() => {
    setSelectedData(null);
  }, []);

  const displayRecord = selectedData ?? latest;
  const isSelected = selectedData !== null;

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0f2d1a 0%, #1B4F72 60%, #145a32 100%)' }}
      >
        <div className="text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="ri-loader-4-line text-white text-5xl animate-spin"></i>
          </div>
          <p className="text-white text-sm font-medium">데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f4f8f5' }}>
      <DashboardHeader deviceId={deviceId} />

      <main className="max-w-screen-xl mx-auto px-4 md:px-6 py-5 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2 text-red-600 text-sm">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-error-warning-line"></i>
            </div>
            {error}
          </div>
        )}

        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            {
              icon: 'ri-leaf-line',
              label: '최신 NDVI',
              value: latest ? latest.ndvi_avg.toFixed(4) : '-',
              color: '#27ae60',
            },
            {
              icon: 'ri-focus-3-line',
              label: '최신 ToF 거리',
              value: latest ? `${Number(latest.tof_center_mm).toFixed(0)} mm` : '-',
              color: '#2E86C1',
            },
            {
              icon: 'ri-plant-line',
              label: '최신 벼 높이',
              value: latest ? `${Number(latest.rice_height_mm).toFixed(0)} mm` : '-',
              color: '#1B4F72',
            },
            {
              icon: 'ri-time-line',
              label: '최신 수신',
              value: latest ? latest.regdate.substring(0, 10) : '-',
              color: '#e67e22',
            },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-4 border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-7 h-7 flex items-center justify-center rounded-lg"
                  style={{ backgroundColor: stat.color + '18' }}
                >
                  <i className={`${stat.icon} text-sm`} style={{ color: stat.color }}></i>
                </div>
                <span className="text-xs text-gray-500">{stat.label}</span>
              </div>
              <p className="font-bold text-gray-800 text-base">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Latest / Selected Data */}
        {displayRecord ? (
          <LatestDataPanel
            record={displayRecord}
            totalCnt={totalCnt}
            housingHeightMm={housingHeightMm}
            isSelected={isSelected}
            onBackToLatest={handleBackToLatest}
          />
        ) : (
          <div className="bg-white rounded-2xl p-10 text-center text-gray-400 border border-gray-100">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <i className="ri-inbox-line text-5xl"></i>
            </div>
            <p>촬영 데이터가 없습니다.</p>
          </div>
        )}

        {/* Camera Angle Control */}
        <CameraControl deviceId={deviceId} />

        {/* Device Remote Settings */}
        <DeviceSettings deviceId={deviceId} housingHeightMm={housingHeightMm} />

        {/* Charts */}
        <ChartPanel deviceId={deviceId} />

        {/* History Table */}
        <HistoryTable
          deviceId={deviceId}
          selectedNo={selectedData?.no ?? null}
          onSelectRecord={handleSelectRecord}
          onTotalCntChange={setTotalCnt}
        />
      </main>

      {/* Footer */}
      <footer className="mt-8 py-5 text-center" style={{ backgroundColor: '#1B4F72' }}>
        <p className="text-white/60 text-xs">
          © 2026 Rice Crop Monitoring System — Device:{' '}
          <span className="text-white font-semibold">{deviceId}</span>
        </p>
      </footer>
    </div>
  );
}